import { jsx as _jsx } from "react/jsx-runtime";
import StatusAlertIcon from "@diligentcorp/atlas-react-icons/StatusAlert.js";
import StatusErrorIcon from "@diligentcorp/atlas-react-icons/StatusError.js";
import StatusInfoIcon from "@diligentcorp/atlas-react-icons/StatusInfo.js";
import StatusSuccessIcon from "@diligentcorp/atlas-react-icons/StatusSuccess.js";
import { deepmerge } from "@mui/utils";
import { applyTokens } from "../../../utilities/applyTokens.js";
const getTokens = (tokens) => {
    return { toast: tokens.component.toast, alert: tokens.component.alert };
};
// This is the "Toast" component in the design system
const toastStyles = ({ tokens }) => {
    const { toast } = getTokens(tokens);
    return {
        borderRadius: toast.borderRadius.value,
        borderLeftWidth: toast.borderLeftWidth.value,
        borderLeftStyle: "solid",
        boxShadow: toast.boxShadow.value.stringValue,
    };
};
const dividedToastStyles = ({ tokens }) => {
    const { toast } = getTokens(tokens);
    return Object.assign(Object.assign({}, toastStyles({ tokens })), { display: "grid", gridTemplateColumns: "auto auto", 
        // The border needs to expand all the way
        // The paddings move to action and icon
        paddingLeft: 0, paddingRight: 0, paddingBottom: 0, 
        // In divided mode, we make actions take up a second row
        "& .MuiAlert-action": {
            gridRow: "2",
            gridColumnStart: "1",
            gridColumnEnd: "3",
            justifyContent: "flex-end",
            paddingRight: toast.divided.paddingRight.value,
            borderTop: `${toast.divided.actions.borderTopWidth.value} solid ${toast.divided.actions.borderTopColor.value}`,
        }, "& .MuiAlert-message": {
            paddingRight: toast.divided.message.paddingRight.value,
            marginBottom: toast.divided.message.paddingBottom.value,
        }, "& .MuiAlert-icon": {
            paddingLeft: toast.divided.paddingLeft.value,
        } });
};
// This is the "Alert" component in the design system
const alertStyles = ({ tokens }) => {
    const { alert } = getTokens(tokens);
    return {
        borderRadius: alert.borderRadius.value,
        borderWidth: alert.borderWidth.value,
        paddingTop: parseInt(alert.paddingVertical.value) - parseInt(alert.borderWidth.value),
        paddingBottom: parseInt(alert.paddingVertical.value) - parseInt(alert.borderWidth.value),
    };
};
const severityStyles = ({ tokens, severity = "success", }) => {
    const { alert } = getTokens(tokens);
    if (!severity || !(severity in alert)) {
        return {};
    }
    const severityTokens = alert[severity];
    return {
        borderColor: severityTokens.borderColor.value,
        "& .MuiAlert-icon": {
            color: severityTokens.icon.color.value,
        },
        "& .MuiAlertTitle-root": {
            color: severityTokens.message.title.color.value,
        },
    };
};
const alert = (tokens) => {
    const { alert } = getTokens(tokens);
    return {
        MuiAlert: {
            defaultProps: {
                iconMapping: {
                    error: _jsx(StatusErrorIcon, {}),
                    info: _jsx(StatusInfoIcon, {}),
                    success: _jsx(StatusSuccessIcon, {}),
                    warning: _jsx(StatusAlertIcon, {}),
                },
            },
            styleOverrides: {
                // defaults
                root: {
                    alignItems: "start",
                    boxShadow: "none",
                    gap: `0 ${alert.gap.value}`,
                    paddingLeft: alert.paddingLeft.value,
                    paddingRight: alert.paddingRight.value,
                    paddingTop: alert.paddingVertical.value,
                    paddingBottom: alert.paddingVertical.value,
                    backgroundColor: alert.backgroundColor.value,
                },
                icon: {
                    lineHeight: alert.icon.height.value,
                    paddingTop: alert.icon.paddingVertical.value,
                    paddingBottom: alert.icon.paddingVertical.value,
                    paddingRight: alert.icon.paddingRight.value,
                    margin: 0,
                    opacity: 1,
                    "& > svg": {
                        width: alert.icon.width.value,
                        height: alert.icon.height.value,
                    },
                },
                message: Object.assign(Object.assign({ flexGrow: 1, paddingTop: alert.message.paddingVertical.value, paddingBottom: alert.message.paddingVertical.value, gap: alert.message.gap.value }, applyTokens(alert.message.text)), { "& .MuiAlertTitle-root": Object.assign({ margin: 0 }, applyTokens(alert.message.title)) }),
                action: {
                    padding: 0,
                    margin: 0,
                },
                // Standard: Atlas Toast
                standard: toastStyles({ tokens }),
                standardSuccess: severityStyles({ tokens, severity: "success" }),
                standardInfo: severityStyles({ tokens, severity: "info" }),
                standardWarning: severityStyles({ tokens, severity: "warning" }),
                standardError: severityStyles({ tokens, severity: "error" }),
                // Outlined: Atlas Alert
                outlined: alertStyles({ tokens }),
                outlinedSuccess: severityStyles({ tokens, severity: "success" }),
                outlinedInfo: severityStyles({ tokens, severity: "info" }),
                outlinedWarning: severityStyles({ tokens, severity: "warning" }),
                outlinedError: severityStyles({ tokens, severity: "error" }),
                // Divided: Atlas Toast with actions on a second row
                divided: ({ ownerState }) => deepmerge(dividedToastStyles({ tokens }), severityStyles({ tokens, severity: ownerState === null || ownerState === void 0 ? void 0 : ownerState.severity })),
            },
        },
    };
};
export default alert;
//# sourceMappingURL=alert.js.map